import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, X } from 'lucide-react';
import {
  formatMatchBreakdownPointsForDisplay,
  formatMatchPercentageForDisplay,
} from '../utils/formatMatchScoreDisplay';

interface ScoreBreakdown {
  productType: { earned: number; max: number; details?: string };
  detailedServices: { earned: number; max: number; details?: string };
  developmentStage: { earned: number; max: number; details?: string };
  supportType: { earned: number; max: number; details?: string };
  batchSize: { earned: number; max: number; details?: string };
  partnerType: { earned: number; max: number; details?: string };
  certifications: { earned: number; max: number; details?: string };
  speedToStart: { earned: number; max: number; details?: string };
  budget?: { earned: number; max: number; details?: string };
  operating_regions?: { earned: number; max: number; details?: string };
  startupFriendly: { earned: number; max: number; details?: string };
  otherDetails: { earned: number; max: number; details?: string };
  cdmoPartnerProfile: { earned: number; max: number; details?: string };
}

interface MatchScoreTooltipProps {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
  position: { top: number; left: number };
  arrowLeft: number;
  placement: 'top' | 'bottom';
  scoreBreakdown: ScoreBreakdown;
  cdmoId?: string;
  contentRef?: React.RefObject<HTMLDivElement>;
}

const MatchScoreTooltip: React.FC<MatchScoreTooltipProps> = ({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  onClose,
  position,
  arrowLeft,
  placement,
  scoreBreakdown,
  cdmoId = '',
  contentRef
}) => {
  if (!isOpen) return null;

  const visibleBreakdownEntries = Object.entries(scoreBreakdown).filter(
    ([, value]) => value.max !== 0
  );

  const categoryLabels: { [key: string]: string } = {
    productType: 'Product Type',
    detailedServices: 'Detailed Services',
    developmentStage: 'Development Stage',
    supportType: 'Support Type',
    batchSize: 'Batch Size',
    partnerType: 'Partner Type',
    cdmoPartnerProfile: 'CDMO Business Profile',
    certifications: 'Certifications',
    speedToStart: 'Project Kickoff',
    budget: 'Budget Compatibility',
    operating_regions: 'Location Preference',
    startupFriendly: 'Startup Friendly',
    otherDetails: 'Additional Details'
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          ref={contentRef}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          id={`score-breakdown-${cdmoId || ''}`}
          role="tooltip"
          style={{
            position: 'fixed',
            top: position.top -5, // Adjust for slight overlap with trigger
            left: position.left,
            zIndex: 9999
          }}
          initial={{ opacity: 0, y: placement === 'top' ? 6 : -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: placement === 'top' ? -6 : 6, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          className="w-80 bg-white/95 backdrop-blur-sm border border-white/50 rounded-xl shadow-xl p-4"
        >
          {/* Arrow */}
          <div
            className={`absolute ${placement === 'top' ? 'bottom-[-6px]' : 'top-[-6px]'}`}
            style={{ left: arrowLeft - 6 }}
          >
            <div
              className="w-3 h-3 rotate-45 bg-white/95 border border-white/50"
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderTopColor: 'transparent',
                borderLeftColor: 'transparent'
              }}
            />
          </div>

          {/* Header */}
          <h4 className="text-sm font-bold text-charcoal mb-3 flex items-center justify-between sticky top-0 bg-white/95 pb-2">
            <span className="flex items-center">
              <Target className="w-4 h-4 mr-2 text-electric-blue" />
              Why This Match?
            </span>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-gray/10 rounded transition-colors flex-shrink-0"
              aria-label="Close tooltip"
            >
              <X className="w-4 h-4 text-slate-gray hover:text-charcoal" />
            </button>
          </h4>

          {/* Score Breakdown Items */}
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-gray/30 scrollbar-track-transparent">
            {visibleBreakdownEntries.length === 0 ? (
              <p className="text-xs text-slate-gray leading-relaxed">
                No ranked criteria are active for the breakdown. Add preferences such as development
                stage, support type, batch size, budget, location, or project kickoff to see how each
                partner scores beyond your hard filters.
              </p>
            ) : (
              visibleBreakdownEntries.map(([key, value]) => {
                const percentage =
                  value.max > 0
                    ? formatMatchPercentageForDisplay((value.earned / value.max) * 100)
                    : 0;

                return (
                  <div key={key} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-charcoal">
                        {categoryLabels[key] || key}
                      </span>
                      <span className="text-xs font-mono text-slate-gray">
                        {formatMatchBreakdownPointsForDisplay(value.earned)}/
                        {formatMatchBreakdownPointsForDisplay(value.max)}
                      </span>
                    </div>
                    <div className="w-full bg-slate-gray/20 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          percentage >= 80
                            ? 'bg-lime-green'
                            : percentage >= 60
                            ? 'bg-electric-blue'
                            : percentage >= 40
                            ? 'bg-soft-teal'
                            : 'bg-slate-gray'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    {value.details && (
                      <div className="text-xs text-slate-gray italic whitespace-pre-line leading-relaxed">
                        {value.details}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="mt-3 pt-2 border-t border-slate-gray/20">
            <div className="text-xs text-slate-gray text-center">
              Hover over score components for details
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MatchScoreTooltip;
